import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { showToast } from 'vant'
import { useUserStore } from '@/store'
import axios from 'axios'
import { captureInviteCode, captureClubToken } from '@/utils/invite'
import { captureInvitePetalToken } from '@/utils/invitePetal'
import { setRouteShare } from '@/utils/wechatShare'
import { initWxNotify } from '@/utils/wxNotify'
import { exchangeSmsLoginTicket } from '@/api/smsAuth'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/home'
  },
  // 首页
  {
    path: '/home',
    name: 'Home',
    component: () => import('@/views/home/index.vue'),
    meta: { title: '首页', requiresAuth: false }
  },
  // 发现（入口聚合页：活动/轨迹/上墙资料库）
  {
    path: '/discover',
    name: 'Discover',
    component: () => import('@/views/discover/index.vue'),
    meta: { title: '发现', requiresAuth: false }
  },
  // AI 红娘（新用户）
  {
    path: '/ai-match',
    name: 'AiMatch',
    component: () => import('@/views/ai-match/index.vue'),
    meta: { title: 'AI 红娘', requiresAuth: false }
  },
  // AI 红娘（老用户二次牵线页）
  {
    path: '/ai-match/old',
    name: 'AiMatchOld',
    component: () => import('@/views/ai-match/old.vue'),
    meta: { title: 'AI 红娘', requiresAuth: true }
  },
  // AI 红娘：历史匹配记录
  {
    path: '/ai-match/history',
    name: 'AiMatchHistory',
    component: () => import('@/views/ai-match/history.vue'),
    meta: { title: '牵线记录', requiresAuth: true }
  },
  // AI 红娘：牵线聊天预约列表（待参加/已参加）
  {
    path: '/ai-match/appointments',
    name: 'AiMatchAppointments',
    component: () => import('@/views/ai-match/appointments-list.vue'),
    meta: { title: '牵线聊天记录', requiresAuth: true }
  },
  // AI 红娘：某条牵线记录详情（总结/客户期望/再次预约）
  {
    path: '/ai-match/record-detail',
    name: 'AiMatchRecordDetail',
    component: () => import('@/views/ai-match/record-detail.vue'),
    meta: { title: '牵线详情', requiresAuth: true }
  },
  // AI 红娘：多轮问答对话页（点击立即体验后进入，全屏无 tabBar）
  {
    path: '/ai-match/qa',
    name: 'AiMatchQa',
    component: () => import('@/views/ai-match/qa.vue'),
    meta: { title: 'AI 红娘正在为您了解心意', requiresAuth: false, hideTabBar: true }
  },
  // AI 红娘：线上约会聊天室（全屏无 tabBar）
  {
    path: '/ai-match/room',
    name: 'AiMatchRoom',
    component: () => import('@/views/ai-match/room.vue'),
    meta: { title: 'AI 红娘・线上面基房间', requiresAuth: false, hideTabBar: true }
  },
  // AI 红娘：谈心对话页（阶段2，布局同 1v1 聊天页，全屏无 tabBar）
  {
    path: '/ai-talk',
    name: 'AiTalk',
    component: () => import('@/views/ai-match/talk.vue'),
    meta: { title: 'AI 情感助理', requiresAuth: true, hideTabBar: true }
  },
  // AI 情感助理：初始介绍页（分享链接游客免登录直达，点「立即开始」才引导登录；
  // 已登录复用 enterAiTalk 既有契约：激活会话直接续聊 / 无激活会话弹花瓣预冻结弹窗）
  {
    path: '/ai-assistant',
    name: 'AiAssistantIntro',
    component: () => import('@/views/ai-match/assistant-intro.vue'),
    meta: { title: 'AI 情感助理', requiresAuth: false, hideTabBar: true }
  },
  // 匹配
  {
    path: '/match',
    name: 'Match',
    component: () => import('@/views/match/index.vue'),
    meta: { title: '匹配', requiresAuth: false }
  },
  {
    path: '/match/detail/:userId',
    name: 'MatchDetail',
    component: () => import('@/views/match/detail.vue'),
    meta: { title: '用户详情', requiresAuth: false }
  },
  // 轨迹（原社群入口改为轨迹，保留社群路由兼容旧链接）
  {
    path: '/trail',
    name: 'Trail',
    component: () => import('@/views/trail/index.vue'),
    meta: { title: '轨迹', requiresAuth: false }
  },
  {
    path: '/trail/detail/:id',
    name: 'TrailDetail',
    component: () => import('@/views/trail/detail.vue'),
    meta: { title: '轨迹详情', requiresAuth: false }
  },
  {
    path: '/trail/publish',
    name: 'TrailPublish',
    component: () => import('@/views/trail/publish.vue'),
    meta: { title: '发布轨迹', requiresAuth: true }
  },
  // 社群（保留旧路由不删）
  {
    path: '/community',
    name: 'Community',
    component: () => import('@/views/community/index.vue'),
    meta: { title: '社群', requiresAuth: false }
  },
  {
    path: '/community/detail/:commId',
    name: 'CommunityDetail',
    component: () => import('@/views/community/detail.vue'),
    meta: { title: '社群详情', requiresAuth: false }
  },
  {
    path: '/community/hosts/:commId',
    name: 'CommunityHosts',
    component: () => import('@/views/community/hosts.vue'),
    meta: { title: '主理人', requiresAuth: false }
  },
  {
    path: '/community/publish',
    name: 'PublishDynamic',
    component: () => import('@/views/community/publish-dynamic.vue'),
    meta: { title: '发布动态', requiresAuth: true }
  },
  // 文章（保留 /article 路由不删，仅移除首页入口；外部链接仍可访问）
  {
    path: '/article',
    name: 'Article',
    component: () => import('@/views/article/index.vue'),
    meta: { title: '情感资料', requiresAuth: false }
  },
  {
    path: '/article/detail/:artId',
    name: 'ArticleDetail',
    component: () => import('@/views/article/detail.vue'),
    meta: { title: '文章详情', requiresAuth: false }
  },
  // 平台公告（首页滚动公告条 → 公告详情）
  {
    path: '/announcement/detail/:id',
    name: 'AnnouncementDetail',
    component: () => import('@/views/announcement/detail.vue'),
    meta: { title: '公告详情', requiresAuth: false, hideTabBar: true }
  },
  // 上墙资料库（内部代号 corner，对客统一称"上墙资料库"，全平台统一展示，禁止租户区分）
  {
    path: '/corner',
    name: 'Corner',
    component: () => import('@/views/corner/index.vue'),
    meta: { title: '上墙资料库', requiresAuth: false }
  },
  {
    path: '/corner/user/:userId',
    name: 'CornerDetail',
    component: () => import('@/views/corner/detail.vue'),
    meta: { title: '用户详情', requiresAuth: false }
  },
  // 活动
  {
    path: '/activity',
    name: 'Activity',
    component: () => import('@/views/activity/index.vue'),
    meta: { title: '活动中心', requiresAuth: false }
  },
  {
    path: '/activity/detail/:actId',
    name: 'ActivityDetail',
    component: () => import('@/views/activity/detail.vue'),
    meta: { title: '活动详情', requiresAuth: false }
  },
  // 游戏（游戏中心 + 现场匹配子页面）
  {
    path: '/game',
    name: 'Game',
    component: () => import('@/views/game/index.vue'),
    meta: { title: '小游戏', requiresAuth: false }
  },
  // 性格测试列表页（从小游戏页独立：性格偏好测评 + MBTI），二级页不展示 TabBar
  {
    path: '/assessment',
    name: 'Assessment',
    component: () => import('@/views/assessment/index.vue'),
    meta: { title: '性格测试', requiresAuth: false, hideTabBar: true }
  },
  {
    path: '/game/room-match',
    name: 'RoomMatchEntry',
    component: () => import('@/views/game/room-match/entry.vue'),
    meta: { title: '现场匹配', requiresAuth: false }
  },
  {
    path: '/game/room-match/create',
    name: 'RoomMatchCreate',
    component: () => import('@/views/game/room-match/create.vue'),
    meta: { title: '创建房间', requiresAuth: false }
  },
  {
    path: '/game/room-match/join',
    name: 'RoomMatchJoin',
    component: () => import('@/views/game/room-match/join.vue'),
    meta: { title: '加入房间', requiresAuth: false }
  },
  {
    path: '/game/room-match/room',
    name: 'RoomMatchRoom',
    component: () => import('@/views/game/room-match/room.vue'),
    meta: { title: '房间详情', requiresAuth: false }
  },
  {
    path: '/game/room-match/result',
    name: 'RoomMatchResult',
    component: () => import('@/views/game/room-match/result.vue'),
    meta: { title: '匹配结果', requiresAuth: false }
  },
  // 现场互选（复用房间体系的专属玩法）
  {
    path: '/game/room-pick',
    name: 'RoomPickEntry',
    component: () => import('@/views/game/room-pick/entry.vue'),
    meta: { title: '现场互选', requiresAuth: false }
  },
  {
    path: '/game/room-pick/create',
    name: 'RoomPickCreate',
    component: () => import('@/views/game/room-pick/create.vue'),
    meta: { title: '创建互选房间', requiresAuth: false }
  },
  {
    path: '/game/room-pick/join',
    name: 'RoomPickJoin',
    component: () => import('@/views/game/room-pick/join.vue'),
    meta: { title: '加入互选房间', requiresAuth: false }
  },
  {
    path: '/game/room-pick/room',
    name: 'RoomPickRoom',
    component: () => import('@/views/game/room-pick/room.vue'),
    meta: { title: '互选房间', requiresAuth: false }
  },
  {
    path: '/game/room-pick/result',
    name: 'RoomPickResult',
    component: () => import('@/views/game/room-pick/result.vue'),
    meta: { title: '互选结果', requiresAuth: false }
  },
  {
    path: '/game/room-pick/host-result',
    name: 'RoomPickHostResult',
    component: () => import('@/views/game/room-pick/host-result.vue'),
    meta: { title: '互选结果明细', requiresAuth: false }
  },
  // 寻宝小队（任务破冰：自动男女平衡分队、任务卡打卡、实时积分榜）
  {
    path: '/game/room-task',
    name: 'RoomTaskEntry',
    component: () => import('@/views/game/room-task/entry.vue'),
    meta: { title: '寻宝小队', requiresAuth: false }
  },
  {
    path: '/game/room-task/create',
    name: 'RoomTaskCreate',
    component: () => import('@/views/game/room-task/create.vue'),
    meta: { title: '创建寻宝房间', requiresAuth: false }
  },
  {
    path: '/game/room-task/join',
    name: 'RoomTaskJoin',
    component: () => import('@/views/game/room-task/join.vue'),
    meta: { title: '加入寻宝房间', requiresAuth: false }
  },
  {
    path: '/game/room-task/room',
    name: 'RoomTaskRoom',
    component: () => import('@/views/game/room-task/room.vue'),
    meta: { title: '寻宝房间', requiresAuth: false }
  },
  {
    path: '/game/room-task/result',
    name: 'RoomTaskResult',
    component: () => import('@/views/game/room-task/result.vue'),
    meta: { title: '寻宝结果', requiresAuth: false }
  },
  // 数字抱团大作战（随机分值限时抱团，定格判定、淘汰晋级、趣味惩罚投票）
  {
    path: '/game/room-hug',
    name: 'RoomHugEntry',
    component: () => import('@/views/game/room-hug/entry.vue'),
    meta: { title: '数字抱团大作战', requiresAuth: false }
  },
  {
    path: '/game/room-hug/create',
    name: 'RoomHugCreate',
    component: () => import('@/views/game/room-hug/create.vue'),
    meta: { title: '创建数字抱团房间', requiresAuth: false }
  },
  {
    path: '/game/room-hug/join',
    name: 'RoomHugJoin',
    component: () => import('@/views/game/room-hug/join.vue'),
    meta: { title: '加入数字抱团房间', requiresAuth: false }
  },
  {
    path: '/game/room-hug/room',
    name: 'RoomHugRoom',
    component: () => import('@/views/game/room-hug/room.vue'),
    meta: { title: '数字抱团房间', requiresAuth: false }
  },
  {
    path: '/game/room-hug/result',
    name: 'RoomHugResult',
    component: () => import('@/views/game/room-hug/result.vue'),
    meta: { title: '数字抱团结果', requiresAuth: false }
  },
  // 谁是卧底（系统自动分词与卧底，线下描述投票、本人确认出局、自动判胜、胜方投票惩罚）
  {
    path: '/game/room-undercover',
    name: 'RoomUndercoverEntry',
    component: () => import('@/views/game/room-uc/entry.vue'),
    meta: { title: '谁是卧底', requiresAuth: false }
  },
  {
    path: '/game/room-undercover/create',
    name: 'RoomUndercoverCreate',
    component: () => import('@/views/game/room-uc/create.vue'),
    meta: { title: '创建谁是卧底房间', requiresAuth: false }
  },
  {
    path: '/game/room-undercover/join',
    name: 'RoomUndercoverJoin',
    component: () => import('@/views/game/room-uc/join.vue'),
    meta: { title: '加入谁是卧底房间', requiresAuth: false }
  },
  {
    path: '/game/room-undercover/room',
    name: 'RoomUndercoverRoom',
    component: () => import('@/views/game/room-uc/room.vue'),
    meta: { title: '谁是卧底房间', requiresAuth: false }
  },
  {
    path: '/game/room-undercover/result',
    name: 'RoomUndercoverResult',
    component: () => import('@/views/game/room-uc/result.vue'),
    meta: { title: '谁是卧底结果', requiresAuth: false }
  },
  // 你画我猜（多人房暖场小游戏，前端 mock 原型）
  {
    path: '/game/draw-guess',
    name: 'DrawGuessEntry',
    component: () => import('@/views/game/draw-guess/index.vue'),
    meta: { title: '你画我猜', requiresAuth: false }
  },
  {
    path: '/game/draw-guess/room',
    name: 'DrawGuessRoom',
    component: () => import('@/views/game/draw-guess/room.vue'),
    meta: { title: '你画我猜', requiresAuth: false, hideTabBar: true }
  },
  {
    path: '/game/draw-guess/result',
    name: 'DrawGuessResult',
    component: () => import('@/views/game/draw-guess/result.vue'),
    meta: { title: '你画我猜结算', requiresAuth: false, hideTabBar: true }
  },
  // 害你在心口难开（多人房暖场小游戏，前端 mock 原型）
  {
    path: '/game/taboo',
    name: 'TabooEntry',
    component: () => import('@/views/game/taboo/index.vue'),
    meta: { title: '害你在心口难开', requiresAuth: false }
  },
  {
    path: '/game/taboo/room',
    name: 'TabooRoom',
    component: () => import('@/views/game/taboo/room.vue'),
    meta: { title: '害你在心口难开', requiresAuth: false, hideTabBar: true }
  },
  {
    path: '/game/taboo/result',
    name: 'TabooResult',
    component: () => import('@/views/game/taboo/result.vue'),
    meta: { title: '害你在心口难开结算', requiresAuth: false, hideTabBar: true }
  },
  // 性格偏好测评（分享链接游客可直达介绍页，点「立即测试」才引导登录；?start=1 登录后直接答题）
  {
    path: '/game/assessment',
    name: 'PersonalityAssessment',
    component: () => import('@/views/game/assessment/index.vue'),
    meta: { title: '性格偏好测评', requiresAuth: false }
  },
  // MBTI 标准版测试（同上，介绍页免登录浏览，登录态下沉到 CTA）
  {
    path: '/game/mbti',
    name: 'MbtiStandardTest',
    component: () => import('@/views/game/mbti/index.vue'),
    meta: { title: 'MBTI 标准版测试', requiresAuth: false }
  },
  // 剧本冒险房
  {
    path: '/game/adventure',
    name: 'AdventureLobby',
    component: () => import('@/views/game/adventure/index.vue'),
    meta: { title: '游戏大厅', requiresAuth: false }
  },
  {
    path: '/game/adventure/enter',
    name: 'AdventureEnter',
    component: () => import('@/views/game/adventure/room.vue'),
    meta: { title: '剧本冒险房', requiresAuth: true, hideTabBar: true }
  },
  {
    path: '/game/adventure/result',
    name: 'AdventureResult',
    component: () => import('@/views/game/adventure/result.vue'),
    meta: { title: '对局结算', requiresAuth: true, hideTabBar: true }
  },
  {
    path: '/game/adventure/guide',
    name: 'AdventureGuide',
    component: () => import('@/views/game/adventure/guide.vue'),
    meta: { title: '游戏说明', requiresAuth: false, hideTabBar: true }
  },
  {
    path: '/game/adventure/badges',
    name: 'AdventureBadges',
    component: () => import('@/views/game/adventure/badges.vue'),
    meta: { title: '我的荣誉墙', requiresAuth: true }
  },
  {
    path: '/game/adventure/leaderboard',
    name: 'AdventureLeaderboard',
    component: () => import('@/views/game/adventure/leaderboard.vue'),
    meta: { title: '月度天梯榜', requiresAuth: true }
  },
  // 聊天
  {
    path: '/chat',
    name: 'ChatList',
    component: () => import('@/views/chat/index.vue'),
    meta: { title: '消息', requiresAuth: true }
  },
  {
    path: '/chat/detail/:peerId',
    name: 'ChatDetail',
    component: () => import('@/views/chat/detail.vue'),
    meta: { title: '聊天', requiresAuth: true }
  },
  {
    path: '/chat/notifications',
    name: 'Notifications',
    component: () => import('@/views/chat/notifications.vue'),
    meta: { title: '系统通知', requiresAuth: true }
  },
  // 我的
  {
    path: '/mine',
    name: 'Mine',
    component: () => import('@/views/mine/index.vue'),
    meta: { title: '我的', requiresAuth: false }
  },
  {
    path: '/mine/profile-edit',
    name: 'ProfileEdit',
    component: () => import('@/views/mine/profile-edit.vue'),
    meta: { title: '编辑资料', requiresAuth: true }
  },
  {
    path: '/mine/preference',
    name: 'Preference',
    component: () => import('@/views/mine/preference.vue'),
    meta: { title: '择偶偏好', requiresAuth: true }
  },
  {
    path: '/mine/vip',
    name: 'VipCenter',
    component: () => import('@/views/mine/vip.vue'),
    meta: { title: '会员中心', requiresAuth: true }
  },
  {
    path: '/mine/likes',
    name: 'Likes',
    component: () => import('@/views/likes/index.vue'),
    meta: { title: '喜欢', requiresAuth: true }
  },
  // 花瓣流水（业务列表：一条卡片=一个业务）
  {
    path: '/mine/petal-records',
    name: 'PetalRecords',
    component: () => import('@/views/mine/petal-records.vue'),
    meta: { title: '花瓣流水', requiresAuth: true }
  },
  // 花瓣业务消耗明细（二级页：该业务全部流水）
  {
    path: '/mine/petal-records/detail/:bizNo',
    name: 'PetalBusinessDetail',
    component: () => import('@/views/mine/petal-business-detail.vue'),
    meta: { title: '消耗明细', requiresAuth: true }
  },
  {
    path: '/mine/follows',
    name: 'Follows',
    component: () => import('@/views/follows/index.vue'),
    meta: { title: '关注与访客', requiresAuth: true }
  },
  {
    path: '/mine/fans',
    name: 'MyFans',
    component: () => import('@/views/fans/index.vue'),
    meta: { title: '我的粉丝', requiresAuth: true }
  },
  {
    path: '/mine/activities',
    name: 'MyActivities',
    component: () => import('@/views/activities/index.vue'),
    meta: { title: '我的活动', requiresAuth: true }
  },
  {
    path: '/mine/trails',
    name: 'MyTrails',
    component: () => import('@/views/mine/trails.vue'),
    meta: { title: '我的动态', requiresAuth: false }
  },
  {
    path: '/mine/profile-card/:userId',
    name: 'ProfileCard',
    component: () => import('@/views/mine/profile-card.vue'),
    meta: { title: '资料卡', requiresAuth: false }
  },
  // 第一主理人工作台
  {
    path: '/club/manage',
    name: 'ClubManage',
    component: () => import('@/views/club/manage.vue'),
    meta: { title: '俱乐部管理', requiresAuth: true }
  },
  {
    path: '/club/members',
    name: 'ClubMembers',
    component: () => import('@/views/club/members.vue'),
    meta: { title: '会员管理', requiresAuth: true }
  },
  {
    path: '/club/card-request/:id',
    name: 'ClubCardRequest',
    component: () => import('@/views/club/card-request.vue'),
    meta: { title: '资料卡授权', requiresAuth: true }
  },
  // 商户
  {
    path: '/merchant',
    name: 'Merchant',
    component: () => import('@/views/merchant/index.vue'),
    meta: { title: '商户管理', requiresAuth: true, role: ['MERCHANT', 'ADMIN'] }
  },
  {
    path: '/merchant/profit',
    name: 'MerchantProfit',
    component: () => import('@/views/merchant/profit.vue'),
    meta: { title: '分润查看', requiresAuth: true, role: ['MERCHANT', 'ADMIN'] }
  },
  // ============ 盲盒CP ============
  {
    path: '/blind/activity',
    name: 'BlindActivity',
    component: () => import('@/views/blind/activity.vue'),
    meta: { title: '每周CP', requiresAuth: false }
  },
  {
    path: '/blind/signup/filter',
    name: 'BlindFilter',
    component: () => import('@/views/blind/filter.vue'),
    meta: { title: '筛选条件', requiresAuth: true }
  },
  {
    path: '/blind/result',
    name: 'BlindResult',
    component: () => import('@/views/blind/result.vue'),
    meta: { title: '匹配结果', requiresAuth: true }
  },
  {
    path: '/blind/history',
    name: 'BlindHistory',
    component: () => import('@/views/blind/history.vue'),
    meta: { title: '每周CP记录', requiresAuth: true }
  },
  // ============ 邀友送花瓣活动 ============
  {
    // 线上资料库介绍落地页（游客可访问，携带 invite_token；立即注册跳注册页）
    path: '/invite/landing',
    name: 'InviteLanding',
    component: () => import('@/views/invite/landing.vue'),
    meta: { title: 'AI线上资料库', requiresAuth: false, hideTabBar: true }
  },
  {
    // 活动详情页（进度/记录/分享/海报；活动结束时同页展示结束态）
    path: '/invite/petal',
    name: 'InvitePetal',
    component: () => import('@/views/invite/index.vue'),
    meta: { title: '邀好友得花瓣', requiresAuth: true, hideTabBar: true }
  },
  // ============ 登录 / 注册 ============
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/mine/login.vue'),
    meta: { title: '登录', requiresAuth: false, guestOnly: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/mine/register.vue'),
    meta: { title: '注册', requiresAuth: false, guestOnly: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/mine/reset-password.vue'),
    meta: { title: '重置密码', requiresAuth: false, guestOnly: true }
  }
]

const router = createRouter({
  // 使用 hash 模式避免 H5 部署刷新 404
  history: createWebHashHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

// 获取后端 BASE_URL
const BASE_URL = import.meta.env.VITE_API_BASE_URL

// 全局路由守卫
router.beforeEach(async (to, _from, next) => {
  const userStore = useUserStore()

  // 1. 设置浏览器标题（站点名统一取 VITE_APP_TITLE，禁止硬编码）
  const title = to.meta?.title as string | undefined
  const appTitle = import.meta.env.VITE_APP_TITLE || 'Aries线上资料库'
  if (title && typeof document !== 'undefined') {
    document.title = `${title} · ${appTitle}`
  }

  // 1.5 短信短链免登票（query: smslt，一次性，5分钟有效）
  // 来源：后端 /s/{code} 302 到 #/业务页?smslt=xxx。必须先换发双令牌，再走后面的鉴权判断。
  const rawSmslt = to.query.smslt
  const smslt = Array.isArray(rawSmslt) ? rawSmslt[0] : rawSmslt
  if (smslt && typeof smslt === 'string') {
    try {
      const res = await exchangeSmsLoginTicket(smslt)
      const auth = res?.data
      if (auth?.accessToken && auth?.refreshToken) {
        userStore.setTokens(auth.accessToken, auth.refreshToken)
        await userStore.fetchMe()
      }
    } catch {
      // ticket 过期/已使用/网络异常：静默，下方清理参数后按正常游客/登录流程走
    }
    // 无论成败都移除一次性凭证，避免停留在地址栏造成分享泄露或重复使用报错
    const cleanQuery = { ...to.query }
    delete cleanQuery.smslt
    return next({ path: to.path, query: cleanQuery, replace: true })
  }

  // ============ AI 牵线入口分流：仅访问 AiMatch（新用户介绍页）时判断 ============
  // 未登录 → 直接放行（展示新用户介绍页 index.vue）
  // 已登录 → 调用后端 has-used 接口，老用户自动跳 /ai-match/old
  // 降级策略：接口异常 / token 失效 → 放行展示新用户介绍页（安全默认值）
  if (to.name === 'AiMatch') {
    if (userStore.accessToken) {
      try {
        const resp = await axios.get(`${BASE_URL}/ai/chat/record/has-used`, {
          headers: { Authorization: `Bearer ${userStore.accessToken}` },
          timeout: 5000
        })
        const used = resp?.data?.data?.used
        if (used === true) {
          return next('/ai-match/old')
        }
      } catch {
        // 接口异常 → 静默降级，放行展示新用户介绍页
      }
    }
    return next()
  }

  // 2. guestOnly=true && 已登录 → 直接跳首页
  if (to.meta?.guestOnly && userStore.isLogin) {
    return next('/home')
  }

  // 3. requiresAuth=true && 无 accessToken → 跳登录
  // 例外：旧分享链接 #/ai-talk 游客落地改走免登录介绍页（介绍页点「立即开始」才引导登录），
  // 避免游客点开分享卡片直接被弹去登录页；已登录用户访问 /ai-talk 行为完全不变
  if (to.name === 'AiTalk' && !userStore.accessToken) {
    return next({ path: '/ai-assistant' })
  }
  if (to.meta?.requiresAuth && !userStore.accessToken) {
    return next({ path: '/login', query: { redirect: to.fullPath } })
  }

  // 4. meta.role 数组存在且当前用户角色不在其中 → 无权限
  const roles = to.meta?.role as string[] | undefined
  if (roles && roles.length > 0) {
    if (!userStore.role || !roles.includes(userStore.role)) {
      showToast({ type: 'fail', message: '暂无权限' })
      return next('/mine')
    }
  }

  next()
})

// 全局错误钩子：发版后用户旧标签页仍持有旧版入口，懒加载已删除的 hash chunk 会 404
// （报 Failed to fetch dynamically imported module），此时自动硬刷新到目标路由一次，
// 拿到最新 index.html 与 chunk，避免白屏/卡死；同一目标只恢复一次，防止异常循环
const CHUNK_RELOAD_FLAG = 'aries_route_chunk_reload'
router.onError((error, to) => {
  const msg = String(error?.message || error || '')
  const isChunkError = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|ChunkLoadError/i.test(
    msg
  )
  if (!isChunkError) return
  const target = to?.fullPath || (window.location.hash ? window.location.hash.slice(1) : '/')
  if (window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === target) return
  window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, target)
  window.location.replace(`${window.location.pathname}${window.location.search}#${target}`)
})

// 全局后置钩子：
// 1. 捕获邀请链接中的 invite_code / ctk（游客双缓存 7 天，老用户自动忽略）
// 2. 按路由注册分享标题/封面（含 OG meta 同步），微信环境再完成 JS-SDK 初始化
router.afterEach((to) => {
  // 已成功到达 chunk 恢复的目标路由 → 清除恢复标记，允许下次发版再次自愈
  if (window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === to.fullPath) {
    window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
  }
  captureInviteCode()
  captureClubToken()
  captureInvitePetalToken()
  // setRouteShare 内部会同步 OG meta，并在微信环境触发 JS-SDK 初始化
  setRouteShare(to.name as string | undefined)
  // 微信公众号通知：静默绑定核销/wxoid 补交/订阅确认队列/公众号入口静默授权（内部全静默）
  initWxNotify()
})

export default router
